import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ImageOff, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api, type RecentCardsResp, type RecentCard } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

/**
 * Carrousel d'attente de l'accueil : les cartes récemment mises à jour
 * (prix + date de dernière MAJ). Affiché tant que le client n'a pas ouvert
 * une carte. Cliquer une tuile ouvre le détail inline (/?card=<id>).
 */
export function CardCarousel() {
  const { t, fmtMoney, fmtDate } = useI18n();
  const [items, setItems] = useState<RecentCard[]>([]);
  const [loading, setLoading] = useState(true);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api<RecentCardsResp>(`/api/cards/recent?limit=20`)
      .then((r) => !cancelled && setItems(r.items ?? []))
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollBy = (dir: 1 | -1) => {
    scroller.current?.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  if (!loading && items.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">
            {t("home.carousel.title") || "Recently updated"}
          </h2>
          <p className="text-xs text-muted-fg">
            {t("home.carousel.subtitle") || "Latest price refreshes across the catalog."}
          </p>
        </div>
        <div className="hidden items-center gap-1 sm:flex">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className="grid h-8 w-8 place-items-center rounded-md border border-border bg-surface text-muted-fg hover:bg-surface-2 hover:text-fg"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className="grid h-8 w-8 place-items-center rounded-md border border-border bg-surface text-muted-fg hover:bg-surface-2 hover:text-fg"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div
        ref={scroller}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:thin]"
      >
        {loading
          ? Array.from({ length: 8 }).map((_, i) => <TileSkeleton key={i} />)
          : items.map((card) => (
              <Tile key={card.id} card={card} fmtMoney={fmtMoney} fmtDate={fmtDate} updatedLabel={t("home.carousel.updated") || "Updated"} />
            ))}
      </div>
    </section>
  );
}

function Tile({
  card,
  fmtMoney,
  fmtDate,
  updatedLabel,
}: {
  card: RecentCard;
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
  updatedLabel: string;
}) {
  return (
    <Link
      to={`/?card=${encodeURIComponent(card.id)}`}
      className="group w-40 shrink-0 snap-start overflow-hidden rounded-lg border border-border bg-surface shadow-soft transition-colors hover:border-ring/60"
    >
      <div className="relative grid aspect-[3/4] place-items-center bg-surface-2">
        {card.image_url ? (
          <img
            src={card.image_url}
            alt={card.name ?? card.id}
            loading="lazy"
            className="h-full w-full object-contain transition-transform duration-200 group-hover:scale-[1.03]"
          />
        ) : (
          <ImageOff className="h-6 w-6 text-muted-fg" />
        )}
        {card.rarity ? (
          <Badge tone="info" variant="soft" className="absolute left-1.5 top-1.5 text-[9px]">
            {card.rarity}
          </Badge>
        ) : null}
      </div>
      <div className="space-y-1 p-2.5">
        <div className="truncate text-sm font-medium leading-tight" title={card.name ?? card.id}>
          {card.name ?? card.id}
        </div>
        <div className="truncate font-mono text-[10px] text-muted-fg">{card.id}</div>
        <div className="pt-0.5 text-base font-semibold tabular-nums">
          {card.price != null ? fmtMoney(card.price, card.currency || "EUR") : "—"}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-fg">
          <Clock className="h-3 w-3 shrink-0" />
          <span className="truncate">{updatedLabel} {fmtDate(card.price_last_update)}</span>
        </div>
      </div>
    </Link>
  );
}

function TileSkeleton() {
  return (
    <div className="w-40 shrink-0 snap-start overflow-hidden rounded-lg border border-border bg-surface">
      <div className="aspect-[3/4] animate-pulse bg-surface-2" />
      <div className="space-y-2 p-2.5">
        <div className="h-3 w-3/4 animate-pulse rounded bg-surface-2" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-surface-2" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-surface-2" />
      </div>
    </div>
  );
}
